import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Space,
  Tag,
  Typography,
  Select,
  Button,
  Popconfirm,
  message,
  Empty,
} from 'antd';
import {
  ThunderboltOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { gamificationApi } from '../../../shared/api/gamificationApi';

const { Text } = Typography;
const { Option } = Select;

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const RolloverTab: React.FC = () => {
  const [rollovers, setRollovers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadRollovers();
  }, [statusFilter]);

  const loadRollovers = async () => {
    try {
      setLoading(true);
      const data = await gamificationApi.getAdminRollovers(statusFilter);
      setRollovers(data);
    } catch (error) {
      message.error('Error al cargar los rollovers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (rolloverId: string) => {
    try {
      await gamificationApi.cancelRollover(rolloverId);
      message.success('Rollover cancelado exitosamente');
      loadRollovers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Error al cancelar el rollover');
    }
  };

  const statusColors: Record<string, string> = {
    active: 'orange',
    completed: 'green',
    cancelled: 'red',
  };

  const cancelReasonLabels: Record<string, string> = {
    fulfilled: 'Cumplido',
    balance_zero: 'Balance cero',
    camino_b: 'Camino B',
    admin: 'Admin',
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
      width: 140,
      ellipsis: true,
      render: (username: string, record: any) => (
        <Text copyable={{ text: record.userId }} style={{ fontSize: '12px' }}>
          {username || record.userId?.substring(0, 8) + '...'}
        </Text>
      ),
    },
    {
      title: 'Depósito',
      dataIndex: 'depositAmount',
      key: 'depositAmount',
      sorter: (a, b) => a.depositAmount - b.depositAmount,
      render: (amount: number) => (
        <Text strong>${amount?.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Bonus',
      dataIndex: 'totalBonusAmount',
      key: 'totalBonusAmount',
      sorter: (a, b) => a.totalBonusAmount - b.totalBonusAmount,
      render: (amount: number) => (
        <Text strong style={{ color: '#52c41a' }}>${amount?.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Mult.',
      dataIndex: 'rolloverMultiplier',
      key: 'rolloverMultiplier',
      width: 70,
      render: (mult: number) => <Tag color="blue">x{mult}</Tag>,
    },
    {
      title: 'Rollover Req.',
      dataIndex: 'rolloverRequired',
      key: 'rolloverRequired',
      sorter: (a, b) => a.rolloverRequired - b.rolloverRequired,
      render: (amount: number) => `$${amount?.toFixed(2)}`,
    },
    {
      title: 'Anti-Lavado Req.',
      dataIndex: 'antiLavadoRequired',
      key: 'antiLavadoRequired',
      sorter: (a, b) => a.antiLavadoRequired - b.antiLavadoRequired,
      render: (amount: number) => `$${amount?.toFixed(2)}`,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => (
        <Space direction="vertical" size={0}>
          <Tag color={statusColors[status] || 'default'}>
            {status === 'active' ? 'Activo' : status === 'completed' ? 'Completado' : 'Cancelado'}
          </Tag>
          {record.cancelReason && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {cancelReasonLabels[record.cancelReason] || record.cancelReason}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Activación',
      dataIndex: 'activatedAt',
      key: 'activatedAt',
      sorter: (a, b) => new Date(a.activatedAt).getTime() - new Date(b.activatedAt).getTime(),
      render: (date: string) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '12px' }}>{new Date(date).toLocaleDateString('es-AR')}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {new Date(date).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Reglas',
      dataIndex: 'appliedRules',
      key: 'appliedRules',
      render: (rules: string[]) => (
        <Space wrap size={2}>
          {rules?.map((rule, idx) => (
            <Tag key={idx} style={{ fontSize: '10px' }}>{rule}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      render: (_, record: any) => (
        record.status === 'active' ? (
          <Popconfirm
            title="¿Cancelar rollover?"
            description="Esta acción no se puede deshacer."
            onConfirm={() => handleCancel(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              size="small"
              icon={<StopOutlined />}
            >
              Cancelar
            </Button>
          </Popconfirm>
        ) : null
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <StyledCard
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#fa541c' }} />
            <span>Rollovers</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              style={{ width: 160 }}
              placeholder="Todos los estados"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="active">Activos</Option>
              <Option value="completed">Completados</Option>
              <Option value="cancelled">Cancelados</Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadRollovers}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={rollovers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} rollover${total !== 1 ? 's' : ''}`,
          }}
          locale={{
            emptyText: (
              <Empty
                description="No hay rollovers registrados"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          scroll={{ x: 1000 }}
        />
      </StyledCard>
    </Space>
  );
};
